import React, { useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Button, Modal, Form, Input, message, Result, Typography } from "antd";
import { CheckCircleFilled, MailOutlined, WindowsOutlined } from "@ant-design/icons";
import Service from "../../service";

const { Text } = Typography;

const OutlookConnectedPage = () => {
  const history = useHistory();
  const { companyslug } = useParams();

  const [testModalOpen, setTestModalOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [form] = Form.useForm();

  const handleSendTestEmail = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    setSending(true);
    try {
      const response = await Service.makeAPICall({
        methodName: Service.postMethod,
        api_url: Service.smtpOutlookTestEmail,
        body: { recipientEmail: values.recipientEmail },
      });

      if (response?.data?.status === 200) {
        message.success(response.data.message || "Test email sent successfully.");
        setTestModalOpen(false);
        form.resetFields();
      } else {
        message.error(response?.data?.message || "Failed to send test email.");
      }
    } catch {
      message.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const handleGoToDashboard = () => {
    history.push(`/${companyslug}/dashboard`);
  };

  const handleGoToSettings = () => {
    history.push(`/${companyslug}/admin/settings`);
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.iconRow}>
          <WindowsOutlined style={styles.msIcon} />
          <CheckCircleFilled style={styles.checkIcon} />
        </div>

        <Result
          status="success"
          title="Microsoft 365 Connected"
          subTitle="Your Outlook mailbox has been linked to WeekMate HRMS. Outgoing emails will now be sent via Microsoft 365."
          extra={null}
          style={{ padding: 0 }}
        />

        <div style={styles.actions}>
          <Button
            type="primary"
            icon={<MailOutlined />}
            size="large"
            onClick={() => setTestModalOpen(true)}
            style={styles.testButton}
          >
            Send Test Email
          </Button>

          <Button size="large" onClick={handleGoToSettings} style={styles.settingsButton}>
            Back to Settings
          </Button>

          <Button size="large" type="default" onClick={handleGoToDashboard}>
            Return to Dashboard
          </Button>
        </div>

        <Text type="secondary" style={styles.helpText}>
          If something looks wrong, go back to Settings and click "Disconnect" to reset.
        </Text>
      </div>

      <Modal
        title={
          <span>
            <MailOutlined style={{ marginRight: 8, color: "#0078d4" }} />
            Send Test Email
          </span>
        }
        open={testModalOpen}
        onCancel={() => {
          setTestModalOpen(false);
          form.resetFields();
        }}
        footer={[
          <Button key="cancel" onClick={() => { setTestModalOpen(false); form.resetFields(); }}>
            Cancel
          </Button>,
          <Button
            key="send"
            type="primary"
            icon={<MailOutlined />}
            loading={sending}
            onClick={handleSendTestEmail}
          >
            Send
          </Button>,
        ]}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="recipientEmail"
            label="Recipient Email"
            rules={[
              { required: true, message: "Please enter a recipient email." },
              { type: "email", message: "Please enter a valid email address." },
            ]}
          >
            <Input
              prefix={<MailOutlined style={{ color: "#bfbfbf" }} />}
              placeholder="e.g. admin@yourcompany.com"
              size="large"
              autoFocus
            />
          </Form.Item>
        </Form>
        <Text type="secondary" style={{ fontSize: 12 }}>
          A test email will be sent from your connected Microsoft 365 mailbox.
        </Text>
      </Modal>
    </div>
  );
};

const styles = {
  page: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f5f7fa",
    padding: "24px",
  },
  card: {
    background: "#fff",
    borderRadius: "12px",
    padding: "48px 40px",
    maxWidth: "480px",
    width: "100%",
    boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
    textAlign: "center",
  },
  iconRow: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "8px",
    marginBottom: "16px",
  },
  msIcon: {
    fontSize: "40px",
    color: "#0078d4",
  },
  checkIcon: {
    fontSize: "22px",
    color: "#52c41a",
    marginTop: "-12px",
  },
  actions: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    marginTop: "32px",
    marginBottom: "20px",
  },
  testButton: {
    background: "#0078d4",
    borderColor: "#0078d4",
  },
  settingsButton: {
    borderColor: "#0078d4",
    color: "#0078d4",
  },
  helpText: {
    fontSize: "12px",
    display: "block",
  },
};

export default OutlookConnectedPage;
